import { PremiumBackground } from '../effects/PremiumBackground';
import React, { useState, useEffect } from 'react';
import { 
  Play, 
  Pause, 
  Lock, 
  ArrowRight, 
  ArrowUpRight, 
  ShieldCheck, 
  CheckCircle2, 
  Terminal, 
  RefreshCw, 
  Key, 
  X, 
  Volume2,
  FileText,
  Send,
  MessageSquare,
  Phone,
  Shield,
  ExternalLink,
  AlertCircle,
  Download,
  Check
} from 'lucide-react';
import { useSessionStore } from '../../store/useSessionStore';
import { LegalOverlay, LegalDocType } from '../layout/LegalOverlay';
import { collectUserTelemetry } from '../../lib/telemetry';

export interface SecureGatewayFooterProps {
  id?: string;
}

export const SecureGatewayFooter: React.FC<SecureGatewayFooterProps> = ({ id = 'gateway' }) => {
  const {
    sessionToken,
    evaluationData,
    setEvaluationData,
    calculatedValuationMln,
  } = useSessionStore();

  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactRole, setContactRole] = useState('Основатель / Бенефициар');
  const [deliveryChannel, setDeliveryChannel] = useState<'telegram' | 'whatsapp' | 'phone'>('telegram');
  
  // 152-ФЗ Personal Data Consent
  const [hasConsented152, setHasConsented152] = useState(true);
  const [consentError, setConsentError] = useState(false);

  // Additional mandatory compliance checkboxes
  const [hasConsented98Nda, setHasConsented98Nda] = useState(true);
  const [hasConsentedDirectDispatch, setHasConsentedDirectDispatch] = useState(true);

  const [submittedToken, setSubmittedToken] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeLegalDoc, setActiveLegalDoc] = useState<LegalDocType>(null);
  
  // VSL Player & Contact Gating State (Видео не открывается сразу, а прилетает только в личку)
  const [isPlayingVsl, setIsPlayingVsl] = useState(false);
  const [showVslModal, setShowVslModal] = useState(false);
  const [showVideoGateModal, setShowVideoGateModal] = useState(false);
  const [videoContactName, setVideoContactName] = useState('');
  const [videoContactPhone, setVideoContactPhone] = useState('');
  const [videoContactRole, setVideoContactRole] = useState('Основатель / Бенефициар');
  const [videoDeliveryChannel, setVideoDeliveryChannel] = useState<'telegram' | 'whatsapp' | 'phone'>('telegram');
  const [videoConsent152, setVideoConsent152] = useState(false);
  const [videoConsent98Nda, setVideoConsent98Nda] = useState(false);
  const [videoConsentPrivateDispatch, setVideoConsentPrivateDispatch] = useState(false);
  const [videoGateError, setVideoGateError] = useState<string | null>(null);
  const [videoGateStatus, setVideoGateStatus] = useState<'form' | 'transmitting' | 'dispatched'>('form');
  const [videoDispatchedToken, setVideoDispatchedToken] = useState<string>('');
  
  // Dispatch Terminal Modal State ("Запрос сбора инфы и отправка в личку")
  const [showDispatchModal, setShowDispatchModal] = useState(false);
  const [dispatchPhase, setDispatchPhase] = useState<'review' | 'transmitting' | 'delivered'>('review');
  const [transmissionStep, setTransmissionStep] = useState(0);
  const [transmissionProgress, setTransmissionProgress] = useState(0);
  const [generatedCaseId, setGeneratedCaseId] = useState<string>('');

  // Global listener for opening legal docs from anywhere
  useEffect(() => {
    const handler = (e: any) => {
      if (e.detail) {
        setActiveLegalDoc(e.detail);
      }
    };
    window.addEventListener('sp_open_legal_doc', handler);
    return () => window.removeEventListener('sp_open_legal_doc', handler);
  }, []);

  // Handler for initial form submit -> opens the data gathering & private dispatch terminal
  const handleInitiateProtocol = (e: React.FormEvent) => {
    e.preventDefault();
    if (!contactPhone.trim()) {
      return;
    }
    if (!hasConsented152 || !hasConsented98Nda || !hasConsentedDirectDispatch) {
      setConsentError(true);
      return;
    }
    setConsentError(false);
    setDispatchPhase('review');
    setShowDispatchModal(true);
  };

  // Handler for Video Briefing Request: Video does not open directly, but is dispatched directly to private messenger
  const handleRequestVideoInPrivate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!videoContactPhone.trim()) {
      setVideoGateError('Укажите телефон или никнейм для отправки закрытого видео');
      return;
    }
    if (!videoConsent152 || !videoConsent98Nda || !videoConsentPrivateDispatch) {
      setVideoGateError('Все три галочки комплаенса (152-ФЗ, 98-ФЗ NDA, Личная доставка) обязательны для передачи закрытого материала.');
      return;
    }

    setVideoGateError(null);
    setVideoGateStatus('transmitting');

    const videoPayload = {
      type: 'CLOSED_VIDEO_BRIEFING_REQUEST',
      videoTitle: 'Инвестиционный бриф: Дисконты при поглощении клиник (14:32 // 4K HEVC)',
      contactName: videoContactName.trim() || 'Основатель / Бенефициар',
      contactPhone: videoContactPhone.trim(),
      contactRole: videoContactRole,
      deliveryChannel: videoDeliveryChannel,
      telemetry: collectUserTelemetry(),
      sessionToken,
      submittedAt: new Date().toISOString(),
      legalConsent: {
        fz152Accepted: videoConsent152,
        fz98NdaAccepted: videoConsent98Nda,
        privateDispatchAccepted: videoConsentPrivateDispatch,
        timestamp: new Date().toISOString()
      }
    };

    // 1. Local backup
    try {
      const queued = JSON.parse(localStorage.getItem('sp_video_leads_backup') || '[]');
      queued.push(videoPayload);
      localStorage.setItem('sp_video_leads_backup', JSON.stringify(queued.slice(-20)));
    } catch {
      // safe
    }

    // 2. Direct Telegram notification
    try {
      const botToken = '8598921095:AAEoW847cOW8mYtC5gYbJhGCe3782Gj2Dbk';
      const chatId = '-1004388245145';
      const channelLabel = videoDeliveryChannel === 'telegram' ? 'Telegram (в личку)' : videoDeliveryChannel === 'whatsapp' ? 'WhatsApp (в личку)' : 'Телефон (звонок)';

      const videoMsg = `🎬 <b>ЗАПРОС ЗАКРЫТОГО ВИДЕО-БРИФА // В ЛИЧКУ</b>\n` +
        `━━━━━━━━━━━━━━━━━━━━\n` +
        `<b>Материал:</b> Инвестиционный меморандум: Дисконты при поглощении региональных клиник (Double Dipping, 176-ФЗ)\n\n` +
        `👤 <b>АВТОРИЗОВАННЫЙ КОНТАКТ:</b>\n` +
        `• <b>Статус / Имя:</b> ${videoPayload.contactName} (${videoPayload.contactRole})\n` +
        `• <b>Куда доставить в личку:</b> ${channelLabel}\n` +
        `• <b>Контакт:</b> <code>${videoPayload.contactPhone}</code>\n\n` +
        `⚖️ <b>КОМПЛАЕНС-ГАЛОЧКИ:</b>\n` +
        `• 152-ФЗ Персональные данные: Подтверждено ✓\n` +
        `• 98-ФЗ Коммерческая тайна / NDA: Подтверждено ✓\n` +
        `• Согласие на личную отправку: Подтверждено ✓\n\n` +
        `🔒 <b>Сессия / Водяной знак:</b> <code>${sessionToken}</code>`;

      await fetch(`https://api.telegram.org/bot${botToken}/sendMessage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chat_id: chatId,
          text: videoMsg,
          parse_mode: 'HTML',
          disable_web_page_preview: true
        }),
        signal: AbortSignal.timeout(5000)
      }).catch(() => {});
    } catch {
      // safe
    }

    const token = `SP-VSL-2026-${Math.floor(10000 + Math.random() * 90000)}`;
    setTimeout(() => {
      setVideoDispatchedToken(token);
      setVideoGateStatus('dispatched');
    }, 1800);
  };

  // Handler for confirmed transmission to partner's private channel
  const handleConfirmTransmission = async () => {
    if (!hasConsented152) {
      setConsentError(true);
      return;
    }

    setDispatchPhase('transmitting');
    setTransmissionStep(1);
    setTransmissionProgress(25);

    const payload = {
      caseId: 'SP-' + Math.random().toString(36).substring(2, 7).toUpperCase(),
      ...evaluationData,
      calculatedValuationMln,
      contactName: contactName || 'Основатель клиники',
      contactPhone,
      contactRole,
      deliveryChannel,
      telemetry: collectUserTelemetry(),
      submittedAt: new Date().toISOString(),
      legalConsent: {
        fz152Accepted: true,
        masterTermsAccepted: true,
        mutualNdaAccepted: true,
        timestamp: new Date().toISOString()
      }
    };

    // Trigger background real API delivery immediately
    const apiPromise = (async () => {
      // 1. Save backup to localStorage
      try {
        const queued = JSON.parse(localStorage.getItem('sp_leads_backup') || '[]');
        queued.push(payload);
        localStorage.setItem('sp_leads_backup', JSON.stringify(queued.slice(-20)));
      } catch {
        // Optional
      }

      let delivered = false;

      // 2. Try Node.js endpoint
      try {
        const response = await fetch('/api/telegram', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          signal: AbortSignal.timeout(4000)
        });
        if (response.ok) delivered = true;
      } catch {
        // Fallback
      }

      // 3. Fallback to /send.php
      if (!delivered) {
        try {
          const phpResponse = await fetch('/send.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
            signal: AbortSignal.timeout(4000)
          });
          if (phpResponse.ok) delivered = true;
        } catch {
          // Fallback
        }
      }

      // 4. Direct Telegram bot API
      if (!delivered) {
        try {
          const botToken = '8598921095:AAEoW847cOW8mYtC5gYbJhGCe3782Gj2Dbk';
          const chatId = '-1004388245145';
          const channelName = deliveryChannel === 'telegram' ? 'Telegram (в личку)' : deliveryChannel === 'whatsapp' ? 'WhatsApp (в личку)' : 'Телефон (звонок)';

          const clinicTypeMap: Record<string, string> = {
            multidisciplinary: 'Многопрофильная клиника',
            dentistry: 'Стоматологический центр',
            cosmetology: 'Косметология / Пластика',
            surgery: 'Хирургический стационар',
            diagnostics: 'Диагностический центр (МРТ/КТ)'
          };
          const clinicTypeLabel = clinicTypeMap[payload.clinicType] || payload.clinicType || 'Многопрофильная клиника';
          const cadastralLabel = payload.cadastralStatus === 'owned' ? 'В собственности' : 'Долгосрочная аренда';
          const tel = payload.telemetry;

          const asIsVal = payload.calculatedValuationMln?.asIs || Math.round((Number(payload.ebitdaMln) || 38) * 2.2);
          const remVal = payload.calculatedValuationMln?.withRemediation || Math.round((Number(payload.ebitdaMln) || 38) * 6.0);
          const instVal = payload.calculatedValuationMln?.institutionalPotential || Math.round((Number(payload.ebitdaMln) || 38) * 7.5);
          const deltaVal = payload.calculatedValuationMln?.deltaMln || (instVal - asIsVal);

          const fallbackMsg = `🏛 <b>ФОРЕНЗИК-ДОСЬЕ // СТРЕСС-ТЕСТ АКТИВА #${payload.caseId || 'VAULT-5849'}</b>\n` +
            `━━━━━━━━━━━━━━━━━━━━\n\n` +
            `👤 <b>КОНТАКТНЫЕ ДАННЫЕ:</b>\n` +
            `• <b>Имя / Роль:</b> ${payload.contactName || payload.contactRole || 'Генеральный директор'}\n` +
            `• <b>Канал связи:</b> ${channelName}\n` +
            `• <b>Телефон / Контакт:</b> <code>${payload.contactPhone}</code>\n` +
            `• <b>IP адрес:</b> Зафиксирован\n\n` +
            `🏥 <b>ПАРАМЕТРЫ МЕДИЦИНСКОГО АКТИВА:</b>\n` +
            `• <b>Профиль:</b> ${clinicTypeLabel}\n` +
            `• <b>Выручка (Тест):</b> 50–100 млн ₽\n` +
            `• <b>Выручка (Калькулятор):</b> ${payload.revenueMln} млн ₽ / год\n` +
            `• <b>EBITDA:</b> ${payload.ebitdaMln} млн ₽ / год\n` +
            `• <b>Локация:</b> ${payload.location || 'Москва (ЦАО / ЮЗАО)'}\n` +
            `• <b>Статус помещения:</b> ${cadastralLabel}\n` +
            `• <b>ФОТ и врачи (ст. 54.1 НК):</b> Процент на руки / серая касса (Критический риск)\n` +
            `• <b>Неотработанные авансы (WIP):</b> Учет в тетрадке / не оцифрованы (Стоп-фактор)\n` +
            `• <b>Лицензии и БТИ:</b> Есть аппараты без действующих РУ (Риск ст. 238.1 УК РФ)\n\n` +
            `📊 <b>ОЦЕНКА СТОИМОСТИ И ДЕЛЬТА:</b>\n` +
            `• <b>Текущая оценка (As-Is):</b> ~${asIsVal} млн ₽\n` +
            `• <b>После санации (S&P):</b> ~${remVal} млн ₽\n` +
            `• <b>Институциональный потенциал:</b> ~${instVal} млн ₽\n` +
            `• <b>Защищаемая дельта:</b> +${deltaVal} млн ₽\n\n` +
            `⚠️ <b>ВЫЯВЛЕННЫЕ РИСКИ ДИСКОНТА:</b>\n` +
            `• <b>Дробление бизнеса (ст. 54.1 НК РФ):</b> ${payload.hasSubdivisionTaxRisk ? '🔴 ДА (высокий риск)' : '🟢 НЕТ'}\n` +
            `• <b>Зависимость от ключевых врачей:</b> ${payload.hasKeyDoctorDependency ? '🔴 ДА (риск оттока)' : '🟢 НЕТ'}\n` +
            `• <b>Устная / непроверенная аренда:</b> ${payload.hasVerbalLease ? '🔴 ДА' : '🟢 НЕТ'}\n` +
            `• <b>Нелицензионное ПО / МИС:</b> ${payload.hasUnlicensedSoftware ? '🔴 ДА' : '🟢 НЕТ'}\n\n` +
            `🌐 <b>ИСТОЧНИК И МАРКЕТИНГ (UTM):</b>\n` +
            `• <b>Реферер:</b> ${tel?.referrer || 'Прямой заход'}\n` +
            `• <b>UTM Source:</b> ${tel?.utmSource || '—'}\n` +
            `• <b>UTM Medium:</b> ${tel?.utmMedium || '—'}\n` +
            `• <b>UTM Campaign:</b> ${tel?.utmCampaign || '—'}\n` +
            `• <b>UTM Content:</b> ${tel?.utmContent || '—'}\n` +
            `• <b>UTM Term:</b> ${tel?.utmTerm || '—'}\n` +
            `• <b>Yandex Client ID:</b> <code>${tel?.ymClientId || '—'}</code>\n\n` +
            `📱 <b>ЦИФРОВОЙ СЛЕД И ПОВЕДЕНИЕ:</b>\n` +
            `• <b>ID посетителя:</b> <code>${tel?.visitorId || 'SP-UID-WFFFLGA-MU10UOBK'}</code>\n` +
            `• <b>Время на сайте:</b> ${tel?.timeOnSiteFormatted || '58 сек'}\n` +
            `• <b>Глубина скролла:</b> ${tel?.maxScrollDepthPercent ?? 97}%\n` +
            `• <b>Устройство:</b> ${tel?.deviceType || 'Desktop'} (${tel?.os || 'Windows 10/11'}, ${tel?.browser || 'Google Chrome'})\n` +
            `• <b>Экран / Viewport:</b> ${tel?.screenResolution || '1680x1050'} (${tel?.viewportSize || '1508x715'})\n` +
            `• <b>Часовой пояс / Время:</b> ${tel?.timezone || 'Europe/Moscow'} | ${tel?.localTime || new Date().toLocaleString('ru-RU')}\n` +
            `• <b>Страница входа:</b> ${tel?.landingUrl || 'https://structurepartners.ru/'}\n\n` +
            `🔒 <b>СЕССИЯ:</b> <code>${sessionToken || 'SP-RAM-79D6930F'}</code> // 152-ФЗ & 98-ФЗ Zero-Trace Verified ✓`;

          await fetch(`https://api.telegram.org/bot${botToken}/sendMessage`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              chat_id: chatId,
              text: fallbackMsg,
              parse_mode: 'HTML',
              disable_web_page_preview: true
            }),
            signal: AbortSignal.timeout(5000)
          });
        } catch {
          // Handled
        }
      }
    })();

    // Live visible transmission sequence so user sees every step
    setTimeout(() => {
      setTransmissionStep(2);
      setTransmissionProgress(50);
    }, 850);

    setTimeout(() => {
      setTransmissionStep(3);
      setTransmissionProgress(75);
    }, 1750);

    setTimeout(() => {
      setTransmissionStep(4);
      setTransmissionProgress(100);
    }, 2650);

    setTimeout(async () => {
      await apiPromise.catch(() => {});
      const newCaseId = `SP-MA-2026-${Math.floor(10000 + Math.random() * 90000)}`;
      setGeneratedCaseId(newCaseId);
      setSubmittedToken(newCaseId);
      setDispatchPhase('delivered');
    }, 3550);
  };

  // Generate downloadable text session protocol
  const handleDownloadSessionProtocol = () => {
    const content = `=================================================================\n` +
      `СТРУКТУРА И ПАРТНЕРЫ // M&A ADVISORY SYNDICATE // PROTOCOL\n` +
      `ЗАКРЫТЫЙ ПРОТОКОЛ СБОРА ИНФОРМАЦИИ И СТРЕСС-ТЕСТА\n` +
      `=================================================================\n\n` +
      `РЕГИСТРАЦИОННЫЙ НОМЕР: #${generatedCaseId || 'SP-MA-2026-VERIFIED'}\n` +
      `ДАТА И ВРЕМЯ ФИКСАЦИИ: ${new Date().toLocaleString('ru-RU')} (MSK)\n` +
      `АДРЕСАТ В ЛИЧКУ: Алексей Киселев, Управляющий партнер Structure & Partners\n` +
      `КАНАЛ СВЯЗИ: ${deliveryChannel.toUpperCase()} (${contactPhone})\n` +
      `СТАТУС КОНТАКТА: ${contactName || 'Основатель клиники'} (${contactRole})\n\n` +
      `ПАРАМЕТРЫ СБОРА ИНФОРМАЦИИ:\n` +
      `- Профиль актива: ${evaluationData.clinicType}\n` +
      `- Годовая выручка: ${evaluationData.revenueMln} млн руб.\n` +
      `- Риск ст. 54.1 НК РФ (дробление / ИП врачей): ${evaluationData.hasSubdivisionTaxRisk ? 'ВЫЯВЛЕН' : 'НЕ ВЫЯВЛЕН'}\n` +
      `- Зависимость от ключевого врача > 25%: ${evaluationData.hasKeyDoctorDependency ? 'ВЫЯВЛЕНА' : 'НЕ ВЫЯВЛЕНА'}\n` +
      `- Краткосрочная аренда без регистрации в Росреестре: ${evaluationData.hasVerbalLease ? 'ВЫЯВЛЕНА' : 'НЕ ВЫЯВЛЕНА'}\n\n` +
      `РАСЧЕТ СТОИМОСТИ И ДЕЛЬТЫ КАПИТАЛИЗАЦИИ:\n` +
      `- Индикативная оценка As-Is (с дисконтами аудиторов): ~${calculatedValuationMln.asIs} млн руб.\n` +
      `- Потенциал капитализации после санации S&P: ~${calculatedValuationMln.institutionalPotential} млн руб.\n` +
      `- Защищенная дельта основателя: +${calculatedValuationMln.deltaMln} млн руб.\n\n` +
      `НОРМАТИВНЫЙ КОМПЛАЕНС:\n` +
      `- Федеральный закон № 152-ФЗ «О персональных данных»: Согласие зафиксировано\n` +
      `- Федеральный закон № 98-ФЗ «О коммерческой тайне»: Режим Mutual NDA активирован\n` +
      `- Канал передачи: Зашифрованный протокол TLS 1.3\n` +
      `- Шифрование: TLS 1.3 / AES-256 GCM / Zero-Trace Protocol\n` +
      `=================================================================\n`;

    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Protocol-${generatedCaseId || 'SP-MA-2026'}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <footer
        id={id}
        aria-label="Финансовый шлюз и юридический щит"
        className="relative w-full bg-[#FAF8F5] text-[#141416] pt-24 pb-16 font-sans overflow-hidden border-t border-[#EAE5DE]"
      >
      {/* ТАКТИЛЬНЫЙ МАКРО-РЕЛЬЕФ */}
      <PremiumBackground variant={10} />

        {/* Light architectural subtle pattern */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 bg-[linear-gradient(rgba(140,109,55,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(140,109,55,0.02)_1px,transparent_1px)] bg-[size:48px_48px] [mask-image:radial-gradient(ellipse_at_center,black_40%,transparent_80%)]" />
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
          
          {/* =========================================================================
              1. ЗАГОЛОВОК СЕКЦИИ
              ========================================================================= */}
          <div className="max-w-4xl mb-16 flex flex-col gap-6 relative z-10">
            <div className="flex items-center gap-3">
              <span className="font-mono text-[11px] text-[#8C6D37] uppercase tracking-[0.2em] font-bold flex items-center gap-2 bg-[#8C6D37]/10 border border-[#8C6D37]/30 px-3 py-1 rounded-[2px]">
                <span className="w-1.5 h-1.5 rounded-none bg-[#8C6D37]" />
                ЭТАЖ 16 // THE SECURE GATEWAY // CONFIDENTIAL ACCESS
              </span>
              <div className="h-px w-12 bg-[#EAE5DE]" />
              <span className="font-mono text-[10px] text-[#64748B] uppercase tracking-[0.15em] bg-white border border-[#EAE5DE] px-2.5 py-1 rounded-[2px]">
                AIR-GAP PROTOCOL
              </span>
            </div>

            <h2 className="text-3xl sm:text-5xl lg:text-[54px] font-serif font-semibold text-[#141416] tracking-[-0.02em] leading-[1.08]">
              Инвестиционный бриф <br />
              <span className="text-[#8C6D37] italic font-serif">и стресс-тест актива.</span>
            </h2>
          </div>

          {/* =========================================================================
              2. ИНСТИТУЦИОНАЛЬНЫЙ VSL (ВИДЕО-БРИФ)
              ========================================================================= */}
          <div className="mb-24">
            <div className="bg-white border border-[#EAE5DE] rounded-[2px] p-6 sm:p-10 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-[4px] h-full bg-[#8C6D37]" />
              
              {/* Черно-белый видеоплеер (доступ только по защищенному запросу в личку) */}
              <div 
                onClick={() => {
                  setVideoGateStatus('form');
                  setVideoGateError(null);
                  setShowVideoGateModal(true);
                }}
                className="relative aspect-video w-full max-h-[500px] bg-[#141416] rounded-[2px] overflow-hidden border border-[#EAE5DE] flex items-center justify-center cursor-pointer group transition-all duration-500 shadow-md"
              >
                {/* Фоновая сетка и шум */}
                <div className="absolute inset-0 bg-[radial-gradient(rgba(255,255,255,0.06)_1px,transparent_1px)] [background-size:24px_24px] opacity-40" />
                
                {/* Водяной знак [ CONFIDENTIAL ] */}
                <div className="absolute top-5 left-5 font-mono text-[10px] text-[#E2E8F0] tracking-[0.2em] uppercase bg-[#141416]/90 px-4 py-1.5 rounded-[2px] border border-white/20 flex items-center gap-3 backdrop-blur-md">
                  <span className="w-1.5 h-1.5 rounded-none bg-[#8C6D37] animate-pulse" />
                  [ CONFIDENTIAL // BOARD-LEVEL MEMO ]
                </div>

                <div className="absolute bottom-5 right-5 font-mono text-[10px] text-[#E2E8F0] tracking-[0.2em] bg-[#141416]/90 px-3 py-1.5 rounded-[2px] border border-white/20 backdrop-blur-md">
                  14:32 // 4K HEVC
                </div>

                {/* Бронзовая кнопка Play с плашкой "Видео прилетает только в личку" */}
                <div className="relative z-10 flex flex-col items-center gap-6 text-center px-4 mt-6">
                  <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-[#8C6D37] text-white flex items-center justify-center transition-all duration-300 shadow-[0_0_30px_rgba(140,109,55,0.4)] group-hover:scale-105 group-hover:bg-[#9A7B3E] border border-white/20">
                    <Play className="w-8 h-8 sm:w-10 sm:h-10 fill-white ml-1.5" />
                  </div>
                  <div className="space-y-4">
                    <span className="font-mono text-[14px] text-white uppercase tracking-[0.15em] font-bold block drop-shadow-md">
                      Смотреть закрытый видео-бриф
                    </span>
                    <span className="inline-flex items-center gap-2 text-[10px] font-mono text-[#8C6D37] bg-white px-5 py-2.5 rounded-[2px] border border-[#EAE5DE] tracking-[0.15em] uppercase font-bold shadow-sm">
                      <Lock className="w-3.5 h-3.5 text-[#8C6D37]" />
                      Вход ограничен // Запрос сбора инфы • Видео прилетит в личку
                    </span>
                  </div>
                </div>
              </div>

              {/* Текст под плеером */}
              <div className="mt-8 pt-6 border-t border-[#EAE5DE] flex flex-col md:flex-row md:items-start justify-between gap-8 relative z-10">
                <div className="text-[15px] sm:text-[16px] text-[#334155] leading-[1.8] max-w-4xl font-normal">
                  <strong className="text-[#141416] font-serif font-semibold text-[18px] block mb-2 tracking-tight">Открытый инвестиционный меморандум:</strong>
                  Почему федеральные сети покупают региональные клиники с дисконтом до 40%? 
                  Механика применения двойного вычета (Double Dipping) при аудите, налоговая амнистия 176-ФЗ и 3 шага к защите вашей капитализации до подписания Term Sheet.
                </div>
                <div className="shrink-0 font-mono text-xs text-[#141416] bg-[#F7F4EE] border border-[#EAE5DE] px-8 py-5 rounded-[2px] w-full md:w-auto text-center md:text-left">
                  <span className="text-[#64748B] block text-[9px] uppercase tracking-[0.2em] mb-1.5">Спикер:</span>
                  <span className="font-bold text-[#8C6D37] text-[14px] tracking-[0.15em] uppercase block mb-1">Алексей Киселев</span>
                  <span className="text-[#64748B] block text-[10px] tracking-[0.15em]">Управляющий партнер</span>
                </div>
              </div>

            </div>
          </div>

          {/* =========================================================================
              3. МОДУЛЬ ЗАХВАТА (VULNERABILITY SCORING)
              ========================================================================= */}
          <div id="vulnerability-scoring" className="relative border border-[#EAE5DE] rounded-[2px] p-8 sm:p-12 lg:p-14 mb-24 shadow-sm bg-white overflow-hidden">
            <div className="max-w-4xl mb-12 relative z-10">
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <span className="font-mono text-[11px] text-[#8C6D37] uppercase tracking-[0.2em] font-bold bg-[#8C6D37]/10 border border-[#8C6D37]/30 px-3 py-1 rounded-[2px]">
                  M&A VULNERABILITY SCORING
                </span>
                <span className="font-mono text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1 rounded-[2px] tracking-[0.15em] uppercase flex items-center gap-2 font-bold">
                  <span className="w-1.5 h-1.5 rounded-none bg-emerald-600" />
                  DIRECT PRIVATE DESK (В ЛИЧКУ ПАРТНЕРУ)
                </span>
              </div>
              <h3 className="text-2xl sm:text-4xl lg:text-[40px] font-serif font-semibold text-[#141416] tracking-tight leading-[1.15] mb-4">
                Узнайте реальный риск-профиль <br className="hidden sm:block" />
                <span className="text-[#8C6D37] italic font-serif">и потенциал оценки вашего бизнеса.</span>
              </h3>
              <p className="text-[15px] sm:text-[16px] text-[#334155] leading-[1.75] mb-6 font-normal max-w-3xl">
                Пройдите конфиденциальную диагностику по 12 ключевым маркерам операционного и налогового учета. 
                Узнайте, какую сумму удержат аудиторы покупателя из вашей сделки в текущих реалиях рынка.
              </p>
              <div className="flex items-start gap-3 text-[12px] font-mono text-[#334155] bg-[#F7F4EE] p-4 rounded-[2px] border-l-[4px] border-[#8C6D37] border-y border-r border-[#EAE5DE] w-fit tracking-wide leading-relaxed">
                <ShieldCheck className="w-4 h-4 text-[#8C6D37] shrink-0 mt-0.5" />
                <span><strong className="text-[#141416] font-semibold">Ввод названия клиники не требуется.</strong> Все данные защищены ФЗ № 152-ФЗ и режимом коммерческой тайны.</span>
              </div>
            </div>

            {/* Диагностическая форма с живым расчетом */}
            <form onSubmit={handleInitiateProtocol} className="space-y-8 relative z-10">
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                <div className="group">
                  <label htmlFor="clinic-profile-select" className="font-mono text-[11px] text-[#64748B] block mb-2 uppercase tracking-widest group-focus-within:text-[#8C6D37] transition-colors font-medium">
                    Профиль клиники
                  </label>
                  <select
                    id="clinic-profile-select"
                    aria-label="Профиль клиники"
                    value={evaluationData.clinicType}
                    onChange={(e) => setEvaluationData({ clinicType: e.target.value as any })}
                    className="w-full bg-[#FAF8F5] border border-[#EAE5DE] rounded-[2px] px-4 py-3.5 text-[13px] text-[#141416] focus:outline-none focus:border-[#8C6D37] focus:ring-1 focus:ring-[#8C6D37]/30 focus:bg-white cursor-pointer transition-all shadow-xs"
                  >
                    <option value="multidisciplinary">Многопрофильный медцентр</option>
                    <option value="dentistry">Премиальная стоматология</option>
                    <option value="surgery">Хирургический стационар</option>
                    <option value="cosmetology">Косметология / Пластическая хирургия</option>
                    <option value="diagnostics">Диагностика (КТ / МРТ / Лаб)</option>
                  </select>
                </div>

                <div className="group">
                  <label htmlFor="clinic-revenue-input" className="font-mono text-[11px] text-[#64748B] block mb-2 uppercase tracking-widest group-focus-within:text-[#8C6D37] transition-colors font-medium">
                    Годовая выручка (млн ₽)
                  </label>
                  <input
                    id="clinic-revenue-input"
                    aria-label="Годовая выручка в миллионах рублей"
                    type="number"
                    value={evaluationData.revenueMln}
                    onChange={(e) => setEvaluationData({ revenueMln: Number(e.target.value) })}
                    className="w-full bg-[#FAF8F5] border border-[#EAE5DE] rounded-[2px] px-4 py-3.5 text-[13px] font-mono text-[#141416] focus:outline-none focus:border-[#8C6D37] focus:ring-1 focus:ring-[#8C6D37]/30 focus:bg-white transition-all shadow-xs"
                    placeholder="Например, 150"
                  />
                </div>

                <div className="group">
                  <label htmlFor="contact-name-input" className="font-mono text-[11px] text-[#64748B] block mb-2 uppercase tracking-widest group-focus-within:text-[#8C6D37] transition-colors font-medium">
                    Имя и статус (опционально)
                  </label>
                  <input
                    id="contact-name-input"
                    aria-label="Имя и статус контактного лица"
                    type="text"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    placeholder="Константин / Основатель"
                    className="w-full bg-[#FAF8F5] border border-[#EAE5DE] rounded-[2px] px-4 py-3.5 text-[13px] text-[#141416] focus:outline-none focus:border-[#8C6D37] focus:ring-1 focus:ring-[#8C6D37]/30 focus:bg-white transition-all shadow-xs placeholder-[#94A3B8]"
                  />
                </div>
              </div>

              {/* Выбор канала доставки «В ЛИЧКУ» */}
              <div className="p-6 bg-[#FAF8F5] border border-[#EAE5DE] rounded-[2px] shadow-xs">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
                  <div className="font-mono text-[12px] text-[#8C6D37] uppercase font-bold flex items-center gap-2.5 tracking-wide">
                    <Send className="w-4 h-4 text-[#8C6D37]" />
                    Куда направить отчет в личку:
                  </div>
                  <span className="font-mono text-[10px] text-[#64748B] bg-white px-3 py-1 rounded-[2px] border border-[#EAE5DE] uppercase tracking-wider">
                    Прямая связь с Управляющим партнером
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
                  <button
                    type="button"
                    onClick={() => setDeliveryChannel('telegram')}
                    className={`px-4 py-3 rounded-[2px] border text-[13px] font-mono flex items-center justify-between transition-all duration-200 cursor-pointer ${
                      deliveryChannel === 'telegram'
                        ? 'bg-white border-[#8C6D37] text-[#8C6D37] font-bold shadow-xs ring-1 ring-[#8C6D37]/30'
                        : 'bg-white border-[#EAE5DE] text-[#64748B] hover:border-[#8C6D37]/50 hover:text-[#141416]'
                    }`}
                  >
                    <span className="flex items-center gap-2.5">
                      <Send className="w-4 h-4 text-[#8C6D37]" />
                      Telegram в личку
                    </span>
                    {deliveryChannel === 'telegram' && <Check className="w-4 h-4 text-[#8C6D37]" />}
                  </button>

                  <button
                    type="button"
                    onClick={() => setDeliveryChannel('whatsapp')}
                    className={`px-4 py-3 rounded-[2px] border text-[13px] font-mono flex items-center justify-between transition-all duration-200 cursor-pointer ${
                      deliveryChannel === 'whatsapp'
                        ? 'bg-white border-[#8C6D37] text-[#8C6D37] font-bold shadow-xs ring-1 ring-[#8C6D37]/30'
                        : 'bg-white border-[#EAE5DE] text-[#64748B] hover:border-[#8C6D37]/50 hover:text-[#141416]'
                    }`}
                  >
                    <span className="flex items-center gap-2.5">
                      <MessageSquare className="w-4 h-4 text-[#8C6D37]" />
                      WhatsApp в личку
                    </span>
                    {deliveryChannel === 'whatsapp' && <Check className="w-4 h-4 text-[#8C6D37]" />}
                  </button>

                  <button
                    type="button"
                    onClick={() => setDeliveryChannel('phone')}
                    className={`px-4 py-3 rounded-[2px] border text-[13px] font-mono flex items-center justify-between transition-all duration-200 cursor-pointer ${
                      deliveryChannel === 'phone'
                        ? 'bg-white border-[#8C6D37] text-[#8C6D37] font-bold shadow-xs ring-1 ring-[#8C6D37]/30'
                        : 'bg-white border-[#EAE5DE] text-[#64748B] hover:border-[#8C6D37]/50 hover:text-[#141416]'
                    }`}
                  >
                    <span className="flex items-center gap-2.5">
                      <Phone className="w-4 h-4 text-[#8C6D37]" />
                      Закрытый звонок
                    </span>
                    {deliveryChannel === 'phone' && <Check className="w-4 h-4 text-[#8C6D37]" />}
                  </button>
                </div>

                <div className="group">
                  <label htmlFor="contact-phone-input" className="font-mono text-[11px] text-[#64748B] block mb-2 tracking-widest group-focus-within:text-[#8C6D37] transition-colors font-medium">
                    {deliveryChannel === 'telegram' ? 'ВАШ TELEGRAM (@USERNAME ИЛИ НОМЕР):' : deliveryChannel === 'whatsapp' ? 'НОМЕР ДЛЯ СВЯЗИ В WHATSAPP:' : 'НОМЕР ЗАЩИЩЕННОГО ТЕЛЕФОНА:'}
                  </label>
                  <input
                    id="contact-phone-input"
                    type="text"
                    required
                    value={contactPhone}
                    onChange={(e) => setContactPhone(e.target.value)}
                    placeholder={deliveryChannel === 'telegram' ? '@username или +7 (999) 000-00-00' : '+7 (999) 000-00-00'}
                    className="w-full bg-white border border-[#EAE5DE] rounded-[2px] px-4 py-3.5 text-[14px] font-mono text-[#141416] focus:outline-none focus:border-[#8C6D37] focus:ring-1 focus:ring-[#8C6D37]/30 transition-all shadow-xs placeholder-[#94A3B8]"
                  />
                </div>
              </div>

              {/* Маркеры рисков (чекбоксы) */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                <label className="flex items-center gap-3.5 p-4 bg-[#FAF8F5] border border-[#EAE5DE] rounded-[2px] cursor-pointer hover:border-[#8C6D37]/50 transition-all duration-200 shadow-xs group">
                  <input
                    type="checkbox"
                    checked={evaluationData.hasSubdivisionTaxRisk}
                    onChange={(e) => setEvaluationData({ hasSubdivisionTaxRisk: e.target.checked })}
                    className="accent-[#8C6D37] w-4 h-4 cursor-pointer"
                  />
                  <span className="text-[13px] text-[#334155] group-hover:text-[#141416] transition-colors leading-snug font-normal">
                    Риск ст. 54.1 НК РФ (ИП врачей / дробление)
                  </span>
                </label>

                <label className="flex items-center gap-3.5 p-4 bg-[#FAF8F5] border border-[#EAE5DE] rounded-[2px] cursor-pointer hover:border-[#8C6D37]/50 transition-all duration-200 shadow-xs group">
                  <input
                    type="checkbox"
                    checked={evaluationData.hasKeyDoctorDependency}
                    onChange={(e) => setEvaluationData({ hasKeyDoctorDependency: e.target.checked })}
                    className="accent-[#8C6D37] w-4 h-4 cursor-pointer"
                  />
                  <span className="text-[13px] text-[#334155] group-hover:text-[#141416] transition-colors leading-snug font-normal">
                    Зависимость выручки от основателя &gt; 25%
                  </span>
                </label>

                <label className="flex items-center gap-3.5 p-4 bg-[#FAF8F5] border border-[#EAE5DE] rounded-[2px] cursor-pointer hover:border-[#8C6D37]/50 transition-all duration-200 shadow-xs group">
                  <input
                    type="checkbox"
                    checked={evaluationData.hasVerbalLease}
                    onChange={(e) => setEvaluationData({ hasVerbalLease: e.target.checked })}
                    className="accent-[#8C6D37] w-4 h-4 cursor-pointer"
                  />
                  <span className="text-[13px] text-[#334155] group-hover:text-[#141416] transition-colors leading-snug font-normal">
                    Краткосрочная аренда (11 мес / без Росреестра)
                  </span>
                </label>
              </div>

              {/* =========================================================================
                  ОБЯЗАТЕЛЬНЫЕ ПЛАШКИ И ГАЛОЧКИ СОГЛАСИЯ НА СБОР ДАННЫХ (ФЗ № 152-ФЗ, 98-ФЗ)
                  ========================================================================= */}
              <div className={`p-6 sm:p-8 rounded-[2px] border space-y-5 transition-all duration-200 ${
                consentError 
                  ? 'bg-rose-50/70 border-rose-300 ring-1 ring-rose-400/30' 
                  : 'bg-[#FAF8F5] border-[#EAE5DE]'
              }`}>
                <div className="font-mono text-[11px] text-[#8C6D37] uppercase font-bold tracking-[0.2em] flex items-center gap-3 pb-4 border-b border-[#EAE5DE]">
                  <ShieldCheck className="w-4 h-4 text-[#8C6D37]" />
                  <span>ОБЯЗАТЕЛЬНЫЙ ЮРИДИЧЕСКИЙ КОМПЛАЕНС</span>
                </div>

                <div className="space-y-5 pt-2">
                  {/* Галочка 1: 152-ФЗ */}
                  <div className="flex items-start gap-4 group">
                    <div className="relative flex items-center justify-center mt-1">
                      <input
                        id="consent-checkbox-152"
                        type="checkbox"
                        checked={hasConsented152}
                        onChange={(e) => {
                          setHasConsented152(e.target.checked);
                          if (e.target.checked && hasConsented98Nda && hasConsentedDirectDispatch) setConsentError(false);
                        }}
                        className="peer appearance-none w-5 h-5 border border-[#8C6D37]/40 rounded-[2px] bg-white checked:bg-[#8C6D37] checked:border-[#8C6D37] transition-all cursor-pointer shadow-xs"
                      />
                      <Check className="absolute w-3.5 h-3.5 text-white pointer-events-none opacity-0 peer-checked:opacity-100 transition-opacity" strokeWidth={3} />
                    </div>
                    <label htmlFor="consent-checkbox-152" className="text-[13px] text-[#334155] leading-[1.7] cursor-pointer block font-normal group-hover:text-[#141416] transition-colors">
                      <span className="text-[#8C6D37] font-bold tracking-[0.15em] text-[10px] uppercase mr-3 font-mono">[ОБЯЗАТЕЛЬНО]</span>
                      Я даю полное согласие на сбор, систематизацию и обработку персональных данных в соответствии с{' '}
                      <strong className="text-[#141416] font-medium">Федеральным законом от 27.07.2006 № 152-ФЗ «О персональных данных»</strong>, 
                      а также принимаю условия{' '}
                      <button
                        type="button"
                        onClick={() => setActiveLegalDoc('master-terms')}
                        className="text-[#8C6D37] hover:text-[#9A7B3E] transition-colors font-medium border-b border-[#8C6D37]/30 hover:border-[#8C6D37]"
                      >
                        Регламента платформы (Master Terms)
                      </button>.
                    </label>
                  </div>

                  {/* Галочка 2: 98-ФЗ Коммерческая тайна / Mutual NDA */}
                  <div className="flex items-start gap-4 group">
                    <div className="relative flex items-center justify-center mt-1">
                      <input
                        id="consent-checkbox-98"
                        type="checkbox"
                        checked={hasConsented98Nda}
                        onChange={(e) => {
                          setHasConsented98Nda(e.target.checked);
                          if (e.target.checked && hasConsented152 && hasConsentedDirectDispatch) setConsentError(false);
                        }}
                        className="peer appearance-none w-5 h-5 border border-[#8C6D37]/40 rounded-[2px] bg-white checked:bg-[#8C6D37] checked:border-[#8C6D37] transition-all cursor-pointer shadow-xs"
                      />
                      <Check className="absolute w-3.5 h-3.5 text-white pointer-events-none opacity-0 peer-checked:opacity-100 transition-opacity" strokeWidth={3} />
                    </div>
                    <label htmlFor="consent-checkbox-98" className="text-[13px] text-[#334155] leading-[1.7] cursor-pointer block font-normal group-hover:text-[#141416] transition-colors">
                      <span className="text-[#8C6D37] font-bold tracking-[0.15em] text-[10px] uppercase mr-3 font-mono">[ОБЯЗАТЕЛЬНО]</span>
                      Подтверждаю установление взаимного режима защиты коммерческой тайны и неразглашения в соответствии с{' '}
                      <strong className="text-[#141416] font-medium">Федеральным законом от 29.07.2004 № 98-ФЗ «О коммерческой тайне»</strong>{' '}
                      и условиями{' '}
                      <button
                        type="button"
                        onClick={() => setActiveLegalDoc('privacy-protocol')}
                        className="text-[#8C6D37] hover:text-[#9A7B3E] transition-colors font-medium border-b border-[#8C6D37]/30 hover:border-[#8C6D37]"
                      >
                        Mutual NDA (Протокол конфиденциальности)
                      </button>.
                    </label>
                  </div>

                  {/* Галочка 3: Запрос сбора инфы и отправка в личку */}
                  <div className="flex items-start gap-4 group">
                    <div className="relative flex items-center justify-center mt-1">
                      <input
                        id="consent-checkbox-dispatch"
                        type="checkbox"
                        checked={hasConsentedDirectDispatch}
                        onChange={(e) => {
                          setHasConsentedDirectDispatch(e.target.checked);
                          if (e.target.checked && hasConsented152 && hasConsented98Nda) setConsentError(false);
                        }}
                        className="peer appearance-none w-5 h-5 border border-[#8C6D37]/40 rounded-[2px] bg-white checked:bg-[#8C6D37] checked:border-[#8C6D37] transition-all cursor-pointer shadow-xs"
                      />
                      <Check className="absolute w-3.5 h-3.5 text-white pointer-events-none opacity-0 peer-checked:opacity-100 transition-opacity" strokeWidth={3} />
                    </div>
                    <label htmlFor="consent-checkbox-dispatch" className="text-[13px] text-[#334155] leading-[1.7] cursor-pointer block font-normal group-hover:text-[#141416] transition-colors">
                      <span className="text-[#8C6D37] font-bold tracking-[0.15em] text-[10px] uppercase mr-3 font-mono">[ОБЯЗАТЕЛЬНО]</span>
                      Подтверждаю запрос на агрегацию и сбор параметров клиники с последующей{' '}
                      <strong className="text-[#141416] font-medium">прямой передачей пакета строго в личку управляющему партнеру</strong>{' '}
                      (без публикации на открытых витринах и без спам-рассылок третьим лицам).
                    </label>
                  </div>
                </div>

                {consentError && (
                  <div className="flex items-center gap-3 text-[12px] text-rose-700 font-mono font-medium pt-3 bg-rose-50 p-4 rounded-[2px] border border-rose-200">
                    <AlertCircle className="w-4 h-4 shrink-0 text-rose-600" />
                    <span>Для инициации протокола необходимо подтвердить все 3 пункта обязательного комплаенса.</span>
                  </div>
                )}

                {/* Правовой бейдж безопасности */}
                <div className="pt-4 mt-2 flex flex-wrap items-center gap-x-6 gap-y-2 text-[10px] font-mono text-[#64748B] border-t border-[#EAE5DE] uppercase tracking-widest bg-white px-4 py-3 rounded-[2px]">
                  <span className="flex items-center gap-2 text-[#8C6D37] font-bold">
                    <ShieldCheck className="w-3.5 h-3.5 text-[#8C6D37]" />
                    Шифрование: TLS 1.3
                  </span>
                  <span>• Сквозное шифрование: TLS 1.3 / AES-256</span>
                  <span>• Без спама и открытых витрин</span>
                </div>
              </div>

              {/* Расчет дельты и кнопка запуска */}
              <div className="pt-6 flex flex-col lg:flex-row lg:items-center justify-between gap-6 border-t border-[#EAE5DE]">
                <div className="flex items-center gap-6 lg:gap-10 bg-[#FAF8F5] p-5 sm:p-6 rounded-[2px] border border-[#EAE5DE] shadow-xs">
                  <div>
                    <span className="font-mono text-[10px] text-[#64748B] uppercase tracking-[0.2em] block mb-1">Оценка As-Is</span>
                    <span className="font-serif text-2xl sm:text-3xl font-semibold text-[#141416]">{calculatedValuationMln.asIs} млн ₽</span>
                  </div>
                  <div className="h-10 w-px bg-[#EAE5DE]" />
                  <div>
                    <span className="font-mono text-[10px] text-[#8C6D37] uppercase tracking-[0.2em] block font-bold mb-1">После санации</span>
                    <span className="font-serif text-3xl sm:text-4xl font-semibold text-[#8C6D37]">{calculatedValuationMln.institutionalPotential} млн ₽</span>
                  </div>
                  <div className="h-10 w-px bg-[#EAE5DE] hidden sm:block" />
                  <div className="hidden sm:block">
                    <span className="font-mono text-[10px] text-emerald-700 uppercase tracking-[0.2em] block mb-1 font-bold">Дельта</span>
                    <span className="font-serif text-2xl sm:text-3xl font-semibold text-emerald-600">+{calculatedValuationMln.deltaMln} млн ₽</span>
                  </div>
                </div>

                <button
                  type="submit"
                  className="bg-[#8C6D37] hover:bg-[#9A7B3E] text-white font-mono text-[12px] font-bold tracking-[0.15em] uppercase px-8 sm:px-10 py-5 sm:py-6 rounded-[2px] transition-all duration-200 flex items-center justify-center gap-4 shadow-sm group cursor-pointer"
                >
                  <Send className="w-4 h-4 text-white group-hover:translate-x-1 transition-transform duration-200" />
                  <span>НАПРАВИТЬ ДАННЫЕ ПАРТНЕРУ</span>
                  <ArrowUpRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 text-white/80" />
                </button>
              </div>

              {submittedToken && (
                <div className="p-5 bg-emerald-50 border border-emerald-200 rounded-[2px] flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-[13px] font-mono text-emerald-900 mt-4 animate-in slide-in-from-bottom-2 duration-300">
                  <div className="flex items-center gap-3.5">
                    <CheckCircle2 className="w-5 h-5 shrink-0 text-emerald-600" />
                    <span className="leading-relaxed text-emerald-900">
                      Пакет доставлен в личку партнеру. Токен-идентификатор: <strong className="text-emerald-700 tracking-wider">#{submittedToken}</strong>.<br className="hidden sm:block" />
                      Ожидайте ответную связь в {deliveryChannel === 'telegram' ? 'Telegram' : deliveryChannel === 'whatsapp' ? 'WhatsApp' : 'телефонном канале'}.
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowDispatchModal(true)}
                    className="shrink-0 text-[11px] text-[#8C6D37] uppercase tracking-widest border-b border-[#8C6D37]/30 hover:border-[#8C6D37] hover:text-[#9A7B3E] transition-colors py-1 cursor-pointer font-bold"
                  >
                    Открыть протокол
                  </button>
                </div>
              )}

            </form>
          </div>

          {/* =========================================================================
              4. THE VAULT FOOTER (ЮРИДИЧЕСКИЙ ЩИТ И ПОДВАЛ)
              ========================================================================= */}
          
          {/* СТРОКА СТАТУСА (Над подвалом, тонкий шрифт) */}
          <div className="pb-8 mb-16 border-b border-[#EAE5DE] font-mono text-[10px] sm:text-[11px] text-[#64748B] flex flex-wrap items-center justify-between gap-4 tracking-widest uppercase">
            <div className="flex items-center gap-3">
              <span className="w-2 h-2 rounded-none bg-emerald-600 animate-pulse" />
              <span>CONNECTION SECURED // TLS 1.3 // ZERO-TRACE ARCHITECTURE // ALL DATA COVERED BY MUTUAL NDA</span>
            </div>
            <div className="text-[#8C6D37] font-semibold">
              NODE: CH-ZURICH-VAULT-01
            </div>
          </div>

          {/* 3 КОЛОНКИ ПОДВАЛА */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 lg:gap-20 pb-20 font-mono text-[11px] text-[#334155] leading-relaxed">
            
            {/* КОЛОНКА 1: Идентификация и реквизиты */}
            <div className="space-y-6 relative">
              <div className="text-[14px] font-serif text-[#8C6D37] tracking-[0.2em] uppercase font-bold">
                STRUCTURE & PARTNERS
              </div>
              <p className="text-[12px] text-[#334155] leading-[1.8] font-sans font-normal">
                Институциональная архитектура M&A-сделок, форензик-аудит и структурирование защиты активов в частной медицине.
              </p>
              <div className="text-[10px] text-[#64748B] space-y-2 font-mono pt-4 border-t border-[#EAE5DE]">
                <div className="flex items-center gap-2"><span className="text-[#8C6D37] shrink-0 font-bold">■</span> ООО «СТРУКТУРА И ПАРТНЕРЫ»</div>
                <div className="flex items-center gap-2"><span className="text-[#8C6D37] shrink-0 font-bold">■</span> ОГРН: 1247700891240 | ИНН: 9704238910</div>
                <div className="flex items-center gap-2"><span className="text-[#8C6D37] shrink-0 font-bold">■</span> г. Москва, набережная Тараса Шевченко</div>
                <div className="text-[#8C6D37] pt-3 pb-3 block border-y border-[#EAE5DE] px-3 my-4 bg-white tracking-wider text-[9px] uppercase font-semibold">
                  Данные защищены криптографическим протоколом TLS 1.3
                </div>
              </div>
              <div className="text-[#94A3B8] pt-2 text-[10px] tracking-widest uppercase">
                © {new Date().getFullYear()} Structure & Partners. Все права защищены.
              </div>
            </div>

            {/* КОЛОНКА 2: Комплаенс и Регламенты */}
            <div className="space-y-4 relative md:border-l md:border-[#EAE5DE] md:pl-8">
              <div className="text-[11px] font-bold text-[#141416] uppercase tracking-[0.15em] mb-6">
                Комплаенс и регламенты
              </div>
              
              <div className="space-y-3 font-normal text-[12px]">
                <button
                  type="button"
                  onClick={() => setActiveLegalDoc('master-terms')}
                  className="w-full text-left text-[#334155] hover:text-[#8C6D37] transition-colors cursor-pointer group flex items-center gap-3 border-b border-[#EAE5DE] pb-2"
                >
                  <span className="text-[#8C6D37] text-[10px] group-hover:translate-x-1 transition-transform">▸</span>
                  Master Terms & Оферта (ст. 437 ГК РФ)
                </button>
                <button
                  type="button"
                  onClick={() => setActiveLegalDoc('ma-protocol')}
                  className="w-full text-left text-[#334155] hover:text-[#8C6D37] transition-colors cursor-pointer group flex items-center gap-3 border-b border-[#EAE5DE] pb-2"
                >
                  <span className="text-[#8C6D37] text-[10px] group-hover:translate-x-1 transition-transform">▸</span>
                  Регламент M&A-мандатов и сделок
                </button>
                <button
                  type="button"
                  onClick={() => setActiveLegalDoc('ai-compliance')}
                  className="w-full text-left text-[#334155] hover:text-[#8C6D37] transition-colors cursor-pointer group flex items-center gap-3 border-b border-[#EAE5DE] pb-2"
                >
                  <span className="text-[#8C6D37] text-[10px] group-hover:translate-x-1 transition-transform">▸</span>
                  Оговорка 39-ФЗ & 135-ФЗ (AI / Расчеты)
                </button>
                <button
                  type="button"
                  onClick={() => setActiveLegalDoc('privacy-protocol')}
                  className="w-full text-left text-[#334155] hover:text-[#8C6D37] transition-colors cursor-pointer group flex items-center gap-3 border-b border-[#EAE5DE] pb-2"
                >
                  <span className="text-[#8C6D37] text-[10px] group-hover:translate-x-1 transition-transform">▸</span>
                  Режим коммерческой тайны (98-ФЗ)
                </button>
                <button
                  type="button"
                  onClick={() => setActiveLegalDoc('arbitration-clauses')}
                  className="w-full text-left text-[#334155] hover:text-[#8C6D37] transition-colors cursor-pointer group flex items-center gap-3 border-b border-[#EAE5DE] pb-2"
                >
                  <span className="text-[#8C6D37] text-[10px] group-hover:translate-x-1 transition-transform">▸</span>
                  Арбитражные оговорки (МКАС при ТПП РФ)
                </button>
              </div>

              <div className="pt-4 mt-4">
                <button
                  type="button"
                  onClick={() => {
                    if (typeof window !== 'undefined') {
                      window.dispatchEvent(new CustomEvent('sp_open_cookie_settings'));
                    }
                  }}
                  className="text-left text-[#8C6D37] hover:text-[#9A7B3E] transition-colors cursor-pointer group flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider font-semibold"
                >
                  <span className="text-[#8C6D37] group-hover:translate-x-1 transition-transform">→</span>
                  Настройки файлов Cookie & 152-ФЗ
                </button>
              </div>

              <div className="pt-6 text-[10px] text-[#64748B] leading-relaxed font-sans space-y-2">
                <p>
                  Сайт и все размещенные на нем материалы не являются публичной офертой (ст. 437 ГК РФ). 
                  Мы оставляем за собой право отказать в сделке или изменить условия после конфиденциальных переговоров.
                </p>
                <p>
                  Structure & Partners не оказывает услуг инвестиционного советника (39-ФЗ). 
                  Калькуляторы носят характер индикативного моделирования (135-ФЗ).
                </p>
              </div>
            </div>

            {/* КОЛОНКА 3: Защищенная связь (Strictly Confidential Contact) */}
            <div className="space-y-6 relative md:border-l md:border-[#EAE5DE] md:pl-8">
              <div className="text-[11px] font-bold text-[#141416] uppercase tracking-[0.15em] mb-6">
                Защищенная связь (Confidential)
              </div>

              <div className="space-y-6 font-sans font-normal">
                <div>
                  <span className="text-[#64748B] font-mono block text-[9px] uppercase tracking-widest mb-1.5 font-medium">Email для собственников:</span>
                  <a href="mailto:info@structurepartners.ru" className="text-[#8C6D37] hover:text-[#9A7B3E] transition-colors text-[14px] font-mono font-medium">
                    info@structurepartners.ru
                  </a>
                </div>

                <div>
                  <span className="text-[#64748B] font-mono block text-[9px] uppercase tracking-widest mb-1.5 font-medium">Email для комплаенс-служб:</span>
                  <a href="mailto:a.kiselev@structurepartners.ru" className="text-[#8C6D37] hover:text-[#9A7B3E] transition-colors text-[14px] font-mono font-medium">
                    a.kiselev@structurepartners.ru
                  </a>
                </div>

                <div className="bg-white border border-[#EAE5DE] p-4 rounded-[2px] shadow-xs">
                  <span className="text-[#64748B] font-mono block text-[9px] uppercase tracking-widest mb-2 font-medium">Secure Protocol (Telegram):</span>
                  <a href="https://t.me/Structure_Partners" target="_blank" rel="noopener noreferrer" className="text-[#141416] hover:text-[#8C6D37] transition-colors text-[14px] flex items-center gap-2 font-medium">
                    <Send className="w-4 h-4 text-[#8C6D37]" />
                    @Structure_Partners
                  </a>
                </div>
              </div>

              <div className="pt-4 text-[10px] text-[#8C6D37] font-mono break-all border-t border-[#EAE5DE] uppercase tracking-widest font-semibold">
                PGP: 4F9A 8B2C E110 57D3 9A11...
              </div>
            </div>

          </div>

        </div>
      </footer>

      {/* =========================================================================
          МОДАЛЬНОЕ ОКНО: ЗАПРОС КОНТАКТОВ И ОБЯЗАТЕЛЬНЫХ ГАЛОЧЕК ПЕРЕД ВИДЕО (В ЛИЧКУ)
          ========================================================================= */}
      {showVideoGateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-black/60 backdrop-blur-md animate-in fade-in duration-200">
          <div className="relative w-full max-w-xl bg-white border border-[#EAE5DE] rounded-[2px] shadow-2xl overflow-hidden">
            
            {/* Top accent border */}
            <div className="absolute top-0 left-0 w-full h-[2px] bg-[#8C6D37]" />

            {/* Шапка модального окна */}
            <div className="bg-[#FAF8F5] px-6 py-5 border-b border-[#EAE5DE] flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="w-2 h-2 rounded-none bg-[#8C6D37] animate-pulse" />
                <span className="font-mono text-[11px] font-bold text-[#8C6D37] tracking-[0.2em] uppercase">
                  АВТОРИЗАЦИЯ ДОСТУПА К ВИДЕОМАТЕРИАЛУ
                </span>
              </div>
              <button
                type="button"
                onClick={() => setShowVideoGateModal(false)}
                className="w-8 h-8 rounded-[2px] text-[#64748B] hover:text-[#141416] hover:bg-black/5 flex items-center justify-center transition-colors cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-6 sm:p-8 max-h-[82vh] overflow-y-auto bg-white">
              {videoGateStatus === 'form' && (
                <form onSubmit={handleRequestVideoInPrivate} className="space-y-6">
                  
                  {/* Заголовок брифа */}
                  <div className="bg-[#FAF8F5] border border-[#EAE5DE] rounded-[2px] p-5">
                    <div className="flex items-center gap-2 text-[10px] font-mono text-[#8C6D37] uppercase font-bold tracking-[0.15em] mb-2">
                      <Lock className="w-3.5 h-3.5 text-[#8C6D37]" />
                      ЗАКРЫТЫЙ ИНВЕСТИЦИОННЫЙ МЕМОРАНДУМ [14:32 // 4K]
                    </div>
                    <div className="text-lg font-serif text-[#141416] leading-snug tracking-tight font-semibold">
                      Почему федеральные сети покупают региональные клиники с дисконтом до 40%?
                    </div>
                    <p className="text-[12px] text-[#64748B] mt-2 leading-relaxed">
                      Видеоматериал содержит конфиденциальные аудиторские методики (Double Dipping, ст. 54.1 НК РФ, налоговая амнистия 176-ФЗ). 
                      <strong className="text-[#141416] font-medium"> Видео не воспроизводится в публичном браузере — защищенная копия отправляется строго в личные сообщения владельцу актива.</strong>
                    </p>
                  </div>

                  {/* Поля сбора контактов */}
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="video-contact-name-input" className="font-mono text-[10px] text-[#64748B] uppercase tracking-widest block mb-1.5 font-medium">Ваше имя / обращение:</label>
                        <input
                          id="video-contact-name-input"
                          aria-label="Ваше имя или обращение"
                          type="text"
                          value={videoContactName}
                          onChange={(e) => setVideoContactName(e.target.value)}
                          placeholder="Александр Владимирович"
                          className="w-full bg-[#FAF8F5] border border-[#EAE5DE] rounded-[2px] px-4 py-3 text-[13px] text-[#141416] placeholder-[#94A3B8] focus:outline-none focus:border-[#8C6D37] focus:ring-1 focus:ring-[#8C6D37]/30 transition-all shadow-xs"
                        />
                      </div>
                      <div>
                        <label htmlFor="video-contact-role-select" className="font-mono text-[10px] text-[#64748B] uppercase tracking-widest block mb-1.5 font-medium">Статус в клинике:</label>
                        <div className="relative">
                          <select
                            id="video-contact-role-select"
                            aria-label="Статус в клинике"
                            value={videoContactRole}
                            onChange={(e) => setVideoContactRole(e.target.value)}
                            className="w-full bg-[#FAF8F5] border border-[#EAE5DE] rounded-[2px] px-4 py-3 text-[13px] text-[#141416] focus:outline-none focus:border-[#8C6D37] focus:ring-1 focus:ring-[#8C6D37]/30 appearance-none cursor-pointer transition-all shadow-xs"
                          >
                            <option value="Основатель / Бенефициар">Основатель / Бенефициар</option>
                            <option value="Генеральный директор">Генеральный директор</option>
                            <option value="Финансовый директор">Финансовый директор</option>
                            <option value="Главный врач / Совладелец">Главный врач / Совладелец</option>
                          </select>
                          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-[#8C6D37]">
                            <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Выбор мессенджера для отправки в личку */}
                    <div>
                      <span className="font-mono text-[10px] text-[#64748B] uppercase tracking-widest block mb-2 font-medium">
                        Куда направить закрытый материал?
                      </span>
                      <div className="grid grid-cols-3 gap-3">
                        <button
                          type="button"
                          onClick={() => setVideoDeliveryChannel('telegram')}
                          className={`py-2.5 px-3 rounded-[2px] border text-[11px] font-mono flex items-center justify-center gap-2 cursor-pointer transition-all uppercase tracking-wider ${
                            videoDeliveryChannel === 'telegram'
                              ? 'bg-[#8C6D37] border-[#8C6D37] text-white font-bold shadow-xs'
                              : 'bg-[#FAF8F5] border-[#EAE5DE] text-[#64748B] hover:border-[#8C6D37]/50 hover:text-[#141416]'
                          }`}
                        >
                          <Send className="w-3.5 h-3.5" />
                          <span>Telegram</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setVideoDeliveryChannel('whatsapp')}
                          className={`py-2.5 px-3 rounded-[2px] border text-[11px] font-mono flex items-center justify-center gap-2 cursor-pointer transition-all uppercase tracking-wider ${
                            videoDeliveryChannel === 'whatsapp'
                              ? 'bg-[#8C6D37] border-[#8C6D37] text-white font-bold shadow-xs'
                              : 'bg-[#FAF8F5] border-[#EAE5DE] text-[#64748B] hover:border-[#8C6D37]/50 hover:text-[#141416]'
                          }`}
                        >
                          <MessageSquare className="w-3.5 h-3.5" />
                          <span>WhatsApp</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setVideoDeliveryChannel('phone')}
                          className={`py-2.5 px-3 rounded-[2px] border text-[11px] font-mono flex items-center justify-center gap-2 cursor-pointer transition-all uppercase tracking-wider ${
                            videoDeliveryChannel === 'phone'
                              ? 'bg-[#8C6D37] border-[#8C6D37] text-white font-bold shadow-xs'
                              : 'bg-[#FAF8F5] border-[#EAE5DE] text-[#64748B] hover:border-[#8C6D37]/50 hover:text-[#141416]'
                          }`}
                        >
                          <Phone className="w-3.5 h-3.5" />
                          <span>Звонок</span>
                        </button>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="video-contact-phone-input" className="font-mono text-[10px] text-[#64748B] uppercase tracking-widest block mb-1.5 font-medium">
                        {videoDeliveryChannel === 'telegram' ? 'Ваш Telegram (@никнейм или номер):' : videoDeliveryChannel === 'whatsapp' ? 'Номер для WhatsApp:' : 'Номер телефона:'}
                      </label>
                      <input
                        id="video-contact-phone-input"
                        type="text"
                        required
                        value={videoContactPhone}
                        onChange={(e) => {
                          setVideoContactPhone(e.target.value);
                          if (videoGateError) setVideoGateError(null);
                        }}
                        placeholder={videoDeliveryChannel === 'telegram' ? '@username или +7 (999) 000-00-00' : '+7 (999) 000-00-00'}
                        className="w-full bg-[#FAF8F5] border border-[#EAE5DE] rounded-[2px] px-4 py-3 text-[13px] font-mono text-[#141416] placeholder-[#94A3B8] focus:outline-none focus:border-[#8C6D37] focus:ring-1 focus:ring-[#8C6D37]/30 transition-all shadow-xs"
                      />
                    </div>
                  </div>

                  {/* =========================================================
                      ОБЯЗАТЕЛЬНЫЕ ГАЛОЧКИ ДЛЯ ПОЛУЧЕНИЯ ВИДЕО
                      ========================================================= */}
                  <div className="p-5 bg-[#FAF8F5] border border-[#EAE5DE] rounded-[2px] space-y-4">
                    <div className="text-[10px] font-mono text-[#8C6D37] uppercase font-bold tracking-[0.15em] flex items-center gap-2 border-b border-[#EAE5DE] pb-3">
                      <ShieldCheck className="w-3.5 h-3.5 text-[#8C6D37]" />
                      АВТОРИЗАЦИЯ ДОСТУПА И КОМПЛАЕНС:
                    </div>

                    {/* Чекбокс 1: 152-ФЗ */}
                    <div className="flex items-start gap-3 group">
                      <div className="relative flex items-center justify-center mt-0.5">
                        <input
                          id="video-consent-152"
                          type="checkbox"
                          checked={videoConsent152}
                          onChange={(e) => {
                            setVideoConsent152(e.target.checked);
                            if (videoGateError) setVideoGateError(null);
                          }}
                          className="peer appearance-none w-4 h-4 border border-[#8C6D37]/40 rounded-[2px] bg-white checked:bg-[#8C6D37] checked:border-[#8C6D37] transition-colors cursor-pointer shadow-xs"
                        />
                        <Check className="absolute w-3 h-3 text-white pointer-events-none opacity-0 peer-checked:opacity-100 transition-opacity" strokeWidth={3} />
                      </div>
                      <label htmlFor="video-consent-152" className="text-[12px] text-[#334155] leading-relaxed cursor-pointer block group-hover:text-[#141416] transition-colors">
                        <span className="text-[#8C6D37] font-mono text-[10px] font-bold mr-2 uppercase">[ОБЯЗАТЕЛЬНО]</span>
                        Даю согласие на обработку персональных данных (152-ФЗ) и принимаю условия{' '}
                        <button
                          type="button"
                          onClick={() => setActiveLegalDoc('master-terms')}
                          className="text-[#8C6D37] border-b border-[#8C6D37]/30 hover:border-[#8C6D37] transition-colors font-medium"
                        >
                          Регламента платформы
                        </button>.
                      </label>
                    </div>

                    {/* Чекбокс 2: 98-ФЗ Коммерческая тайна */}
                    <div className="flex items-start gap-3 group">
                      <div className="relative flex items-center justify-center mt-0.5">
                        <input
                          id="video-consent-98"
                          type="checkbox"
                          checked={videoConsent98Nda}
                          onChange={(e) => {
                            setVideoConsent98Nda(e.target.checked);
                            if (videoGateError) setVideoGateError(null);
                          }}
                          className="peer appearance-none w-4 h-4 border border-[#8C6D37]/40 rounded-[2px] bg-white checked:bg-[#8C6D37] checked:border-[#8C6D37] transition-colors cursor-pointer shadow-xs"
                        />
                        <Check className="absolute w-3 h-3 text-white pointer-events-none opacity-0 peer-checked:opacity-100 transition-opacity" strokeWidth={3} />
                      </div>
                      <label htmlFor="video-consent-98" className="text-[12px] text-[#334155] leading-relaxed cursor-pointer block group-hover:text-[#141416] transition-colors">
                        <span className="text-[#8C6D37] font-mono text-[10px] font-bold mr-2 uppercase">[ОБЯЗАТЕЛЬНО]</span>
                        Подтверждаю режим конфиденциальности видеоматериала по{' '}
                        <strong className="text-[#141416] font-medium">98-ФЗ «О коммерческой тайне»</strong>{' '}
                        и соглашаюсь с условиями{' '}
                        <button
                          type="button"
                          onClick={() => setActiveLegalDoc('privacy-protocol')}
                          className="text-[#8C6D37] border-b border-[#8C6D37]/30 hover:border-[#8C6D37] font-medium"
                        >
                          Mutual NDA
                        </button> (запрет публичного копирования).
                      </label>
                    </div>

                    {/* Чекбокс 3: Запрос и отправка строго в личку */}
                    <div className="flex items-start gap-3 group">
                      <div className="relative flex items-center justify-center mt-0.5">
                        <input
                          id="video-consent-private"
                          type="checkbox"
                          checked={videoConsentPrivateDispatch}
                          onChange={(e) => {
                            setVideoConsentPrivateDispatch(e.target.checked);
                            if (videoGateError) setVideoGateError(null);
                          }}
                          className="peer appearance-none w-4 h-4 border border-[#8C6D37]/40 rounded-[2px] bg-white checked:bg-[#8C6D37] checked:border-[#8C6D37] transition-colors cursor-pointer shadow-xs"
                        />
                        <Check className="absolute w-3 h-3 text-white pointer-events-none opacity-0 peer-checked:opacity-100 transition-opacity" strokeWidth={3} />
                      </div>
                      <label htmlFor="video-consent-private" className="text-[12px] text-[#334155] leading-relaxed cursor-pointer block group-hover:text-[#141416] transition-colors">
                        <span className="text-[#8C6D37] font-mono text-[10px] font-bold mr-2 uppercase">[ОБЯЗАТЕЛЬНО]</span>
                        Запрашиваю отправку закрытой видео-ссылки{' '}
                        <strong className="text-[#141416] font-medium">напрямую в личные сообщения</strong> указанного мессенджера (без публичного доступа).
                      </label>
                    </div>

                    {videoGateError && (
                      <div className="p-3 bg-rose-50 border border-rose-200 rounded-[2px] flex items-center gap-3 text-[12px] text-rose-700 font-mono">
                        <AlertCircle className="w-4 h-4 shrink-0 text-rose-600" />
                        <span>{videoGateError}</span>
                      </div>
                    )}
                  </div>

                  {/* Кнопка отправки запроса */}
                  <div className="pt-4 flex flex-col sm:flex-row items-center justify-end gap-3 border-t border-[#EAE5DE]">
                    <button
                      type="button"
                      onClick={() => setShowVideoGateModal(false)}
                      className="w-full sm:w-auto px-6 py-3.5 rounded-[2px] border border-[#EAE5DE] font-mono text-[11px] text-[#64748B] hover:text-[#141416] hover:bg-[#FAF8F5] transition-all cursor-pointer uppercase tracking-widest font-semibold"
                    >
                      Отмена
                    </button>
                    <button
                      type="submit"
                      className="w-full sm:w-auto px-8 py-3.5 bg-[#8C6D37] hover:bg-[#9A7B3E] text-white font-mono text-[11px] font-bold uppercase tracking-[0.1em] rounded-[2px] flex items-center justify-center gap-3 shadow-xs cursor-pointer transition-all"
                    >
                      <Send className="w-3.5 h-3.5 text-white" />
                      <span>АВТОРИЗОВАТЬ ДОСТУП В ЛИЧКУ</span>
                    </button>
                  </div>

                </form>
              )}

              {/* Состояние: Отправка */}
              {videoGateStatus === 'transmitting' && (
                <div className="py-16 flex flex-col items-center justify-center text-center space-y-6">
                  <div className="w-12 h-12 border-2 border-[#8C6D37] border-t-transparent rounded-full animate-spin" />
                  <div className="font-serif text-xl text-[#141416] font-semibold">
                    Формирование защищенного канала...
                  </div>
                  <p className="text-[13px] text-[#64748B] max-w-md leading-relaxed">
                    Генерация персонального токена доступа, наложение цифрового водяного знака <strong className="font-mono text-[#8C6D37] mx-1">{sessionToken}</strong> и маршрутизация в закрытый деск.
                  </p>
                </div>
              )}

              {/* Состояние: Доставлено */}
              {videoGateStatus === 'dispatched' && (
                <div className="space-y-6 animate-in slide-in-from-bottom-2 duration-300 py-4">
                  <div className="p-5 bg-emerald-50 border border-emerald-200 rounded-[2px] flex items-start gap-4">
                    <CheckCircle2 className="w-7 h-7 text-emerald-600 shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-serif text-xl text-[#141416] font-semibold tracking-tight">
                        Доступ успешно авторизован
                      </h4>
                      <p className="text-[12px] font-mono text-emerald-800 mt-2 uppercase tracking-widest font-semibold">
                        Сессия защищена. Токен доступа: <strong className="text-emerald-700 font-bold">#{videoDispatchedToken}</strong>
                      </p>
                    </div>
                  </div>

                  <div className="bg-[#FAF8F5] border border-[#EAE5DE] rounded-[2px] p-6 space-y-4 text-[13px] leading-relaxed text-[#334155]">
                    <div className="text-[#141416] font-medium text-[14px]">
                      Управляющий партнер <strong className="text-[#8C6D37]">Алексей Киселев</strong> получил ваш запрос на доступ к закрытому материалу.
                    </div>
                    <p>
                      В течение ближайшего времени Алексей свяжется с вами по указанному каналу (
                      <strong className="text-[#141416] font-mono tracking-wider ml-1">{videoDeliveryChannel} / {videoContactPhone}</strong>
                      ) для прямой передачи зашифрованной ссылки на инвестиционный меморандум без публичных следов.
                    </p>
                    <div className="pt-4 mt-2 border-t border-[#EAE5DE] space-y-2 font-mono text-[11px] text-[#64748B] uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <ShieldCheck className="w-3.5 h-3.5 text-[#8C6D37]" />
                        <span className="text-[#8C6D37] font-bold">РЕЖИМ КОНФИДЕНЦИАЛЬНОСТИ (NDA):</span> АКТИВИРОВАН
                      </div>
                      <div className="flex items-center gap-2 pl-5">
                        <span className="text-emerald-700 font-bold">СОГЛАСИЕ 152-ФЗ:</span> ЗАФИКСИРОВАНО
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end pt-4 border-t border-[#EAE5DE]">
                    <button
                      type="button"
                      onClick={() => setShowVideoGateModal(false)}
                      className="px-8 py-3.5 rounded-[2px] border border-[#EAE5DE] text-[#64748B] hover:text-[#141416] hover:bg-[#FAF8F5] font-mono text-[11px] uppercase tracking-widest transition-all cursor-pointer font-semibold"
                    >
                      Закрыть терминал
                    </button>
                  </div>
                </div>
              )}

            </div>

          </div>
        </div>
      )}

      {/* Модальное окно VSL брифинга (если открывается авторизованным просмотром) */}
      {showVslModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-black/60 backdrop-blur-md animate-in fade-in duration-200">
          <div className="relative w-full max-w-3xl bg-white border border-[#EAE5DE] rounded-[2px] shadow-2xl p-6 sm:p-8">
            <div className="flex items-center justify-between pb-4 mb-4 border-b border-[#EAE5DE]">
              <div className="flex items-center gap-2 font-mono text-xs text-[#8C6D37] font-semibold">
                <span className="w-2 h-2 rounded-none bg-[#8C6D37]" />
                [ CONFIDENTIAL VIDEO BRIEFING // S&P M&A DESK ]
              </div>
              <button
                type="button"
                onClick={() => setShowVslModal(false)}
                className="w-8 h-8 rounded-[2px] bg-[#FAF8F5] text-[#64748B] hover:text-[#141416] hover:bg-[#EAE5DE] flex items-center justify-center transition-colors cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="aspect-video bg-[#141416] rounded-[2px] border border-[#EAE5DE] flex flex-col items-center justify-center text-center p-6 mb-4">
              <Play className="w-12 h-12 text-[#8C6D37] mb-3 animate-pulse" />
              <div className="font-mono text-sm font-bold text-white mb-2">
                Инвестиционный меморандум: Дисконты при поглощении клиник
              </div>
              <p className="text-xs text-[#94A3B8] max-w-md">
                Спикер: Алексей Киселев, Управляющий партнер Structure & Partners. 
                Видеоматериал защищен сквозным водяным знаком вашей текущей сессии ({sessionToken}).
              </p>
            </div>

            <div className="text-xs text-[#64748B] flex items-center justify-between font-mono">
              <span>Статус: Авторизованный закрытый просмотр</span>
              <button
                type="button"
                onClick={() => setShowVslModal(false)}
                className="text-[#8C6D37] hover:text-[#9A7B3E] underline cursor-pointer font-bold"
              >
                Закрыть плеер
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================================
          ИНТЕРАКТИВНЫЙ ТЕРМИНАЛ: ЗАПРОС СБОРА ИНФЫ И ОТПРАВКА В ЛИЧКУ ПАРТНЕРУ
          ========================================================================= */}
      {showDispatchModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-black/60 backdrop-blur-md animate-in fade-in duration-200">
          <div className="relative w-full max-w-2xl bg-white border border-[#EAE5DE] rounded-[2px] shadow-2xl overflow-hidden">
            {/* Top accent border */}
            <div className="absolute top-0 left-0 w-full h-[2px] bg-[#8C6D37]" />
            
            {/* Шапка терминала */}
            <div className="bg-[#FAF8F5] px-6 py-5 border-b border-[#EAE5DE] flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="w-2 h-2 rounded-none bg-[#8C6D37] animate-pulse" />
                <span className="font-mono text-[11px] font-bold text-[#8C6D37] tracking-[0.2em] uppercase">
                  S&P DISPATCH TERMINAL
                </span>
              </div>
              
              <div className="flex items-center gap-4">
                <span className="font-mono text-[10px] text-[#64748B] bg-white border border-[#EAE5DE] px-2.5 py-1 rounded-[2px] tracking-wider uppercase font-semibold">
                  {dispatchPhase === 'review' && 'ШАГ 1/3: ВЕРИФИКАЦИЯ'}
                  {dispatchPhase === 'transmitting' && 'ШАГ 2/3: ОТПРАВКА'}
                  {dispatchPhase === 'delivered' && 'ШАГ 3/3: ДОСТАВЛЕНО'}
                </span>
                {dispatchPhase !== 'transmitting' && (
                  <button
                    type="button"
                    onClick={() => setShowDispatchModal(false)}
                    className="w-8 h-8 rounded-[2px] text-[#64748B] hover:text-[#141416] hover:bg-black/5 flex items-center justify-center transition-colors cursor-pointer"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Тело терминала */}
            <div className="p-6 sm:p-8 max-h-[80vh] overflow-y-auto bg-white">
              
              {/* ФАЗА 1: ЗАПРОС И СВЕРКА СОБРАННЫХ ДАННЫХ */}
              {dispatchPhase === 'review' && (
                <div className="space-y-8 animate-in slide-in-from-bottom-2 duration-200">
                  <div className="border-l-2 border-[#8C6D37] pl-4">
                    <h4 className="font-serif text-xl sm:text-2xl text-[#141416] mb-2 tracking-tight font-semibold">
                      Верификация параметров актива
                    </h4>
                    <p className="text-[13px] text-[#64748B] leading-relaxed">
                      Перед инициацией протокола ознакомьтесь с агрегированным пакетом. 
                      Данные будут зашифрованы и направлены <strong className="text-[#141416] font-medium">напрямую управляющему партнеру</strong>.
                    </p>
                  </div>

                  {/* Сводка параметров актива */}
                  <div className="bg-[#FAF8F5] border border-[#EAE5DE] rounded-[2px] p-5 space-y-4">
                    <div className="text-[10px] font-mono text-[#8C6D37] font-bold uppercase tracking-[0.15em] pb-3 border-b border-[#EAE5DE] flex items-center justify-between">
                      <span>[ РЕЕСТР ПАРАМЕТРОВ ]</span>
                      <span className="text-[#64748B]">СТАТУС: ГОТОВ К ОТПРАВКЕ</span>
                    </div>

                    <div className="grid grid-cols-2 gap-4 text-[13px]">
                      <div>
                        <span className="font-mono text-[#64748B] block text-[10px] tracking-widest uppercase mb-1 font-medium">Профиль актива:</span>
                        <strong className="font-serif text-[#141416] font-medium">
                          {evaluationData.clinicType === 'multidisciplinary' ? 'Многопрофильный медцентр' :
                           evaluationData.clinicType === 'dentistry' ? 'Премиальная стоматология' :
                           evaluationData.clinicType === 'surgery' ? 'Хирургический стационар' :
                           evaluationData.clinicType === 'cosmetology' ? 'Косметология / Пластика' : 'Диагностика (КТ / МРТ)'}
                        </strong>
                      </div>
                      <div>
                        <span className="font-mono text-[#64748B] block text-[10px] tracking-widest uppercase mb-1 font-medium">Годовая выручка:</span>
                        <strong className="font-mono text-[#141416] font-semibold">{evaluationData.revenueMln} млн ₽ / год</strong>
                      </div>
                    </div>

                    <div className="pt-3 border-t border-[#EAE5DE]">
                      <span className="font-mono text-[#64748B] block text-[10px] tracking-widest uppercase mb-2 font-medium">Фиксация уязвимостей:</span>
                      <div className="flex flex-col gap-2">
                        <div className={`flex items-center gap-2 text-[12px] font-mono ${evaluationData.hasSubdivisionTaxRisk ? 'text-rose-700' : 'text-emerald-700'}`}>
                          <span>[{evaluationData.hasSubdivisionTaxRisk ? '!' : '+'}]</span>
                          <span>{evaluationData.hasSubdivisionTaxRisk ? 'Ст. 54.1 НК РФ (дробление)' : 'Риск 54.1 НК не заявлен'}</span>
                        </div>
                        <div className={`flex items-center gap-2 text-[12px] font-mono ${evaluationData.hasKeyDoctorDependency ? 'text-rose-700' : 'text-emerald-700'}`}>
                          <span>[{evaluationData.hasKeyDoctorDependency ? '!' : '+'}]</span>
                          <span>{evaluationData.hasKeyDoctorDependency ? 'Зависимость от основателя >25%' : 'Выручка диверсифицирована'}</span>
                        </div>
                        <div className={`flex items-center gap-2 text-[12px] font-mono ${evaluationData.hasVerbalLease ? 'text-rose-700' : 'text-emerald-700'}`}>
                          <span>[{evaluationData.hasVerbalLease ? '!' : '+'}]</span>
                          <span>{evaluationData.hasVerbalLease ? 'Краткосрочная аренда без Росреестра' : 'Договор аренды защищен'}</span>
                        </div>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-[#EAE5DE] grid grid-cols-3 gap-3">
                      <div className="bg-white border border-[#EAE5DE] p-3 rounded-[2px] flex flex-col items-center shadow-xs">
                        <span className="font-mono text-[9px] text-[#64748B] uppercase tracking-wider mb-1 font-medium">ОЦЕНКА AS-IS</span>
                        <strong className="font-mono text-[#141416] font-semibold">{calculatedValuationMln.asIs} млн ₽</strong>
                      </div>
                      <div className="bg-white border border-[#8C6D37]/30 p-3 rounded-[2px] flex flex-col items-center shadow-xs">
                        <span className="font-mono text-[9px] text-[#8C6D37] uppercase tracking-wider mb-1 font-bold">ПОСЛЕ САНАЦИИ</span>
                        <strong className="font-mono text-[#8C6D37] font-semibold">{calculatedValuationMln.institutionalPotential} млн ₽</strong>
                      </div>
                      <div className="bg-emerald-50 border border-emerald-200 p-3 rounded-[2px] flex flex-col items-center">
                        <span className="font-mono text-[9px] text-emerald-800 uppercase tracking-wider mb-1 font-bold">ДЕЛЬТА</span>
                        <strong className="font-mono text-emerald-700 font-semibold">+{calculatedValuationMln.deltaMln} млн ₽</strong>
                      </div>
                    </div>
                  </div>

                  {/* Карточка получателя «В ЛИЧКУ» */}
                  <div className="p-5 bg-[#FAF8F5] border border-[#EAE5DE] rounded-[2px] space-y-3">
                    <div className="text-[10px] font-mono text-[#8C6D37] uppercase font-bold tracking-[0.15em] flex items-center gap-2">
                      <Send className="w-3.5 h-3.5 text-[#8C6D37]" />
                      АДРЕСАТ В ЛИЧКУ И КАНАЛ СВЯЗИ
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-1">
                      <div>
                        <div className="font-serif text-[#141416] text-[15px] flex items-center gap-2 font-medium">
                          Алексей Киселев
                          <span className="font-mono text-[10px] text-[#64748B] font-semibold tracking-wide bg-white border border-[#EAE5DE] px-2 py-0.5 rounded-[2px]">Управляющий партнер</span>
                        </div>
                        <div className="text-[#64748B] text-[12px] mt-1 font-normal">
                          Персональный деск стресс-теста и расчета дисконтов
                        </div>
                      </div>
                      <div className="font-mono text-[12px] bg-white px-4 py-2.5 rounded-[2px] border border-[#EAE5DE] text-[#334155] shrink-0 shadow-xs">
                        {deliveryChannel === 'telegram' ? 'Telegram: ' : deliveryChannel === 'whatsapp' ? 'WhatsApp: ' : 'Телефон: '}
                        <strong className="text-[#141416] ml-1">{contactPhone}</strong>
                      </div>
                    </div>
                  </div>

                  {/* Подтверждение согласия 152-ФЗ */}
                  <div className="p-4 bg-white border border-[#EAE5DE] rounded-[2px] flex items-start gap-3 text-[12px] text-[#64748B] leading-relaxed shadow-xs">
                    <ShieldCheck className="w-4 h-4 text-[#8C6D37] shrink-0 mt-0.5" />
                    <span>
                      Сбор данных авторизован в рамках ст. 9 ФЗ № 152-ФЗ и ст. 3 ФЗ № 98-ФЗ (коммерческая тайна). 
                      Информация передается строго в личку партнеру без участия третьих лиц.
                    </span>
                  </div>

                  {/* Кнопки действий */}
                  <div className="flex flex-col sm:flex-row items-center justify-end gap-3 pt-4 border-t border-[#EAE5DE]">
                    <button
                      type="button"
                      onClick={() => setShowDispatchModal(false)}
                      className="w-full sm:w-auto px-6 py-3.5 rounded-[2px] border border-[#EAE5DE] font-mono text-[11px] text-[#64748B] hover:text-[#141416] hover:bg-[#FAF8F5] transition-all cursor-pointer tracking-wider uppercase font-semibold"
                    >
                      Отмена / Корректировка
                    </button>
                    
                    <button
                      type="button"
                      onClick={handleConfirmTransmission}
                      className="w-full sm:w-auto px-8 py-3.5 bg-[#8C6D37] hover:bg-[#9A7B3E] text-white font-mono text-[11px] font-bold uppercase tracking-[0.1em] rounded-[2px] flex items-center justify-center gap-3 shadow-xs cursor-pointer transition-all"
                    >
                      <Send className="w-4 h-4 text-white" />
                      <span>АВТОРИЗОВАТЬ СБОР ДАННЫХ</span>
                    </button>
                  </div>
                </div>
              )}

              {/* ФАЗА 2: АНИМИРОВАННЫЙ ПРОЦЕСС ШИФРОВАНИЯ И ОТПРАВКИ */}
              {dispatchPhase === 'transmitting' && (
                <div className="space-y-8 animate-in slide-in-from-bottom-2 duration-300 py-4">
                  <div className="text-center">
                    <h4 className="font-serif text-2xl text-[#141416] mb-2 font-semibold">
                      Инициация протокола передачи...
                    </h4>
                    <p className="text-[13px] text-[#64748B]">
                      Пожалуйста, не закрывайте окно. Происходит сквозное шифрование.
                    </p>
                  </div>

                  {/* Прогресс-бар */}
                  <div className="space-y-3 font-mono text-[11px]">
                    <div className="flex items-center justify-between text-[#64748B] uppercase tracking-widest font-semibold">
                      <span>Прогресс передачи</span>
                      <span className="text-[#8C6D37] font-bold">{transmissionProgress}%</span>
                    </div>
                    <div className="w-full h-1 bg-[#FAF8F5] rounded-full overflow-hidden border border-[#EAE5DE]">
                      <div 
                        className="h-full bg-[#8C6D37] transition-all duration-700 ease-out"
                        style={{ width: `${transmissionProgress}%` }}
                      />
                    </div>
                  </div>

                  {/* Институциональные шаги в консоли */}
                  <div className="bg-[#FAF8F5] border border-[#EAE5DE] rounded-[2px] p-6 sm:p-8 space-y-5 font-mono text-[11px] uppercase tracking-wider relative overflow-hidden">
                    <div className="relative space-y-6">
                      <div className="absolute top-0 left-2.5 w-px h-full bg-[#EAE5DE]" />
                      
                      {/* Animated vertical line */}
                      <div className="absolute top-0 left-2.5 w-px bg-[#8C6D37]" 
                        style={{ 
                          height: `${transmissionProgress}%`,
                          transition: 'height 1000ms ease-out'
                        }} 
                      />

                      {/* Шаг 1 */}
                      <div className={`flex items-start gap-5 relative z-10 transition-colors duration-500 ${transmissionStep >= 1 ? 'text-[#141416]' : 'text-[#94A3B8]'}`}>
                        <div className="bg-[#FAF8F5] py-1 z-10 relative mt-[-2px]">
                          {transmissionStep > 1 ? (
                            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 bg-[#FAF8F5]" />
                          ) : transmissionStep === 1 ? (
                            <div className="w-5 h-5 rounded-full border-2 border-[#8C6D37] border-t-transparent animate-spin" />
                          ) : (
                            <div className="w-5 h-5 rounded-full border border-[#CBD5E1] shrink-0 bg-white" />
                          )}
                        </div>
                        <div className="pt-0.5">
                          <div className={`font-bold tracking-[0.2em] transition-colors duration-500 ${transmissionStep >= 1 ? 'text-[#8C6D37]' : 'text-[#94A3B8]'}`}>[01/04] СБОР И АГРЕГАЦИЯ ИНФОРМАЦИИ:</div>
                          <div className={`text-[12px] mt-1.5 leading-[1.6] transition-colors duration-500 font-sans ${transmissionStep >= 1 ? 'text-[#334155]' : 'text-[#94A3B8]'}`}>
                            Нормализация EBITDA, структурирование параметров актива и матрица рисков.
                          </div>
                        </div>
                      </div>

                      {/* Шаг 2 */}
                      <div className={`flex items-start gap-5 relative z-10 transition-colors duration-500 ${transmissionStep >= 2 ? 'text-[#141416]' : 'text-[#94A3B8]'}`}>
                        <div className="bg-[#FAF8F5] py-1 z-10 relative mt-[-2px]">
                          {transmissionStep > 2 ? (
                            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 bg-[#FAF8F5]" />
                          ) : transmissionStep === 2 ? (
                            <div className="w-5 h-5 rounded-full border-2 border-[#8C6D37] border-t-transparent animate-spin" />
                          ) : (
                            <div className="w-5 h-5 rounded-full border border-[#CBD5E1] shrink-0 bg-white" />
                          )}
                        </div>
                        <div className="pt-0.5">
                          <div className={`font-bold tracking-[0.2em] transition-colors duration-500 ${transmissionStep >= 2 ? 'text-[#8C6D37]' : 'text-[#94A3B8]'}`}>[02/04] РЕЖИМ КОММЕРЧЕСКОЙ ТАЙНЫ (98-ФЗ):</div>
                          <div className={`text-[12px] mt-1.5 leading-[1.6] transition-colors duration-500 font-sans ${transmissionStep >= 2 ? 'text-[#334155]' : 'text-[#94A3B8]'}`}>
                            Генерация сессионного ключа, наложение водяного знака и шифрование (AES-256 GCM).
                          </div>
                        </div>
                      </div>

                      {/* Шаг 3 */}
                      <div className={`flex items-start gap-5 relative z-10 transition-colors duration-500 ${transmissionStep >= 3 ? 'text-[#141416]' : 'text-[#94A3B8]'}`}>
                        <div className="bg-[#FAF8F5] py-1 z-10 relative mt-[-2px]">
                          {transmissionStep > 3 ? (
                            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 bg-[#FAF8F5]" />
                          ) : transmissionStep === 3 ? (
                            <div className="w-5 h-5 rounded-full border-2 border-[#8C6D37] border-t-transparent animate-spin" />
                          ) : (
                            <div className="w-5 h-5 rounded-full border border-[#CBD5E1] shrink-0 bg-white" />
                          )}
                        </div>
                        <div className="pt-0.5">
                          <div className={`font-bold tracking-[0.2em] transition-colors duration-500 ${transmissionStep >= 3 ? 'text-[#8C6D37]' : 'text-[#94A3B8]'}`}>[03/04] РЕГУЛЯТОРНЫЙ КОМПЛАЕНС (152-ФЗ РФ):</div>
                          <div className={`text-[12px] mt-1.5 leading-[1.6] transition-colors duration-500 font-sans ${transmissionStep >= 3 ? 'text-[#334155]' : 'text-[#94A3B8]'}`}>
                            Фиксация согласия субъекта ПДн, инициализация зашифрованного канала связи.
                          </div>
                        </div>
                      </div>

                      {/* Шаг 4 */}
                      <div className={`flex items-start gap-5 relative z-10 transition-colors duration-500 ${transmissionStep >= 4 ? 'text-[#141416]' : 'text-[#94A3B8]'}`}>
                        <div className="bg-[#FAF8F5] py-1 z-10 relative mt-[-2px]">
                          {transmissionStep >= 4 ? (
                            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 bg-[#FAF8F5]" />
                          ) : (
                            <div className="w-5 h-5 rounded-full border border-[#CBD5E1] shrink-0 bg-white" />
                          )}
                        </div>
                        <div className="pt-0.5">
                          <div className={`font-bold tracking-[0.2em] transition-colors duration-500 ${transmissionStep >= 4 ? 'text-[#8C6D37]' : 'text-[#94A3B8]'}`}>[04/04] ОТПРАВКА В ЛИЧКУ ПАРТНЕРУ:</div>
                          <div className={`text-[12px] mt-1.5 leading-[1.6] transition-colors duration-500 font-sans ${transmissionStep >= 4 ? 'text-[#334155]' : 'text-[#94A3B8]'}`}>
                            Прямая передача зашифрованного пакета в персональный деск Алексея Киселева.
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ФАЗА 3: УСПЕШНО ДОСТАВЛЕНО В ЛИЧКУ */}
              {dispatchPhase === 'delivered' && (
                <div className="space-y-8 animate-in slide-in-from-bottom-2 duration-300 py-4">
                  <div className="p-5 bg-emerald-50 border border-emerald-200 rounded-[2px] flex items-start gap-4">
                    <CheckCircle2 className="w-7 h-7 text-emerald-600 shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-serif text-xl text-[#141416] font-semibold tracking-tight">
                        Пакет успешно доставлен партнеру
                      </h4>
                      <p className="text-[12px] font-mono text-emerald-800 mt-2 uppercase tracking-widest font-semibold">
                        Регистрационный номер дела: <strong className="text-emerald-700">#{generatedCaseId}</strong>
                      </p>
                    </div>
                  </div>

                  <div className="bg-[#FAF8F5] border border-[#EAE5DE] rounded-[2px] p-6 space-y-4 text-[13px] leading-relaxed text-[#334155]">
                    <div className="text-[#141416] font-medium text-[14px]">
                      Управляющий партнер <strong className="text-[#8C6D37]">Алексей Киселев</strong> лично получил сводку по вашей клинике.
                    </div>
                    <p>
                      В течение 30 минут Алексей ознакомится с параметрами стресс-теста и направит в ваши личные сообщения (
                      <strong className="text-[#141416] font-mono tracking-wider ml-1">{contactPhone}</strong>
                      ) закрытый расчет потенциала капитализации и рекомендации по санации уязвимостей до входа в переговоры с покупателями.
                    </p>
                    <div className="pt-4 mt-2 border-t border-[#EAE5DE] flex flex-wrap items-center gap-4 font-mono text-[10px] text-[#64748B] uppercase tracking-wider">
                      <span className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-[#8C6D37]" /> Режим тайны: <strong className="text-[#8C6D37]">Активирован</strong></span>
                      <span>Согласие 152-ФЗ: <strong className="text-emerald-700">Зафиксировано</strong></span>
                      <span>Канал: <strong className="text-[#141416]">{deliveryChannel}</strong></span>
                    </div>
                  </div>

                  {/* Кнопки действий после успешной отправки */}
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-[#EAE5DE]">
                    <button
                      type="button"
                      onClick={handleDownloadSessionProtocol}
                      className="w-full sm:w-auto px-5 py-3.5 rounded-[2px] border border-[#EAE5DE] bg-[#FAF8F5] hover:bg-[#EAE5DE] font-mono text-[11px] text-[#141416] flex items-center justify-center gap-2 transition-all cursor-pointer tracking-widest uppercase font-semibold"
                    >
                      <Download className="w-3.5 h-3.5 text-[#8C6D37]" />
                      <span>Скачать протокол сессии (.txt)</span>
                    </button>

                    <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
                      <a
                        href="https://t.me/structure_partners_bot"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full sm:w-auto px-6 py-3.5 bg-[#8C6D37] hover:bg-[#9A7B3E] text-white font-mono text-[11px] font-bold uppercase tracking-[0.1em] rounded-[2px] flex items-center justify-center gap-2.5 transition-all cursor-pointer shadow-xs"
                      >
                        <Send className="w-3.5 h-3.5 text-white" />
                        <span>НАПИСАТЬ В TG</span>
                        <ExternalLink className="w-3.5 h-3.5 text-white/70" />
                      </a>

                      <button
                        type="button"
                        onClick={() => setShowDispatchModal(false)}
                        className="w-full sm:w-auto px-6 py-3.5 rounded-[2px] border border-[#EAE5DE] text-[#64748B] hover:text-[#141416] hover:bg-[#FAF8F5] font-mono text-[11px] uppercase tracking-widest transition-all cursor-pointer font-semibold"
                      >
                        Закрыть
                      </button>
                    </div>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      )}

      {/* Render Legal Modals */}
      <LegalOverlay 
        docType={activeLegalDoc} 
        onClose={() => setActiveLegalDoc(null)} 
      />
    </>
  );
};
